/* Atelier: interactie voor de eigen secties. Geen dependencies. */

class AtCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    if (!this.track) return;
    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    this.track.addEventListener('scroll', () => this.update(), { passive: true });
    new ResizeObserver(() => this.update()).observe(this.track);
    this.buildDots();
    this.update();
  }

  buildDots() {
    this.dots = this.querySelector('[data-dots]');
    if (!this.dots) return;
    this.slides = [...this.track.querySelectorAll('.at-carousel__slide')];
    this.dots.innerHTML = '';
    this.slides.forEach((slide, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'at-dots__dot';
      dot.setAttribute('aria-label', `${i + 1} / ${this.slides.length}`);
      dot.addEventListener('click', () =>
        this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' })
      );
      this.dots.appendChild(dot);
    });
  }

  updateDots() {
    if (!this.dots || !this.slides?.length) return;
    const left = this.track.scrollLeft;
    let active = 0;
    this.slides.forEach((slide, i) => {
      if (Math.abs(slide.offsetLeft - this.track.offsetLeft - left) < Math.abs(this.slides[active].offsetLeft - this.track.offsetLeft - left)) active = i;
    });
    [...this.dots.children].forEach((dot, i) => dot.classList.toggle('is-active', i === active));
  }

  step() {
    const slide = this.track.querySelector('.at-carousel__slide');
    if (!slide) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return slide.getBoundingClientRect().width + gap;
  }

  go(dir) {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const loop = this.hasAttribute('data-loop');
    let target = this.track.scrollLeft + dir * this.step();
    if (loop && dir > 0 && this.track.scrollLeft >= max - 2) target = 0;
    if (loop && dir < 0 && this.track.scrollLeft <= 2) target = max;
    this.track.scrollTo({ left: target, behavior: 'smooth' });
  }

  update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const scrollable = max > 2;
    this.classList.toggle('is-scrollable', scrollable);
    this.updateDots();
    if (this.hasAttribute('data-loop')) return;
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
    if (this.next) this.next.disabled = this.track.scrollLeft >= max - 2;
  }
}
if (!customElements.get('at-carousel')) customElements.define('at-carousel', AtCarousel);

class AtSlideshow {
  constructor(root) {
    this.root = root;
    this.slides = [...root.querySelectorAll('.at-slideshow__slide')];
    this.dots = [...root.querySelectorAll('.at-slideshow__dot')];
    this.index = 0;
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.show(+dot.dataset.go, true)));
    root.querySelector('[data-scroll-next]')?.addEventListener('click', () => {
      const section = root.closest('.shopify-section');
      const next = section?.nextElementSibling;
      const top = (next || section).getBoundingClientRect().top + window.scrollY - (next ? this.headerOffset() : -section.offsetHeight);
      window.scrollTo({ top, behavior: 'smooth' });
    });
    if (root.dataset.autoplay === 'true' && this.slides.length > 1 && !Shopify.designMode) this.play();
    root.addEventListener('mouseenter', () => this.stop());
    root.addEventListener('mouseleave', () => root.dataset.autoplay === 'true' && this.play());
    this.addSwipe();
  }

  headerOffset() {
    const header = document.querySelector('.section-header');
    return header && getComputedStyle(header).position === 'sticky' ? header.offsetHeight : 0;
  }

  show(i, user) {
    const next = (i + this.slides.length) % this.slides.length;
    if (next === this.index) return;
    const from = this.slides[this.index];
    const to = this.slides[next];
    this.index = next;
    this.dots.forEach((d, n) => d.classList.toggle('is-active', n === next));
    // Eerst de tekst van de huidige slide laten wegvallen, daarna de nieuwe slide laten binnenkomen
    from.classList.add('is-leaving');
    clearTimeout(this.leaveTimer);
    this.leaveTimer = setTimeout(() => {
      from.classList.remove('is-active', 'is-leaving');
      to.classList.add('is-active');
    }, 250);
    if (user && this.timer) this.play();
  }

  play() {
    this.stop();
    this.timer = setInterval(() => this.show(this.index + 1), +this.root.dataset.speed || 6000);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  addSwipe() {
    let x0 = null;
    this.root.addEventListener('touchstart', (e) => (x0 = e.touches[0].clientX), { passive: true });
    this.root.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 40) this.show(this.index + (dx < 0 ? 1 : -1), true);
      x0 = null;
    });
  }
}

class AtLook extends HTMLElement {
  connectedCallback() {
    this.products = [...this.querySelectorAll('.at-look__product')];
    this.mobile = window.matchMedia('(max-width: 989px)');
    this.querySelectorAll('[data-go]').forEach((btn) =>
      btn.addEventListener('click', () => {
        this.show(+btn.dataset.go);
        if (this.mobile.matches && btn.classList.contains('at-look__dot')) this.open();
      })
    );
    this.querySelector('[data-look-open]')?.addEventListener('click', () => this.open());
    this.querySelector('[data-look-close]')?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => e.key === 'Escape' && this.close());
  }

  open() {
    this.classList.add('is-open');
    this.querySelector('[data-look-close]')?.focus();
  }

  close() {
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    this.querySelector('[data-look-open]')?.focus();
  }

  show(i) {
    this.products.forEach((p, n) => p.classList.toggle('is-active', n === i));
    this.querySelectorAll('[data-go]').forEach((b) => b.classList.toggle('is-active', +b.dataset.go === i));
  }
}
if (!customElements.get('at-look')) customElements.define('at-look', AtLook);

class AtCompare extends HTMLElement {
  connectedCallback() {
    const range = this.querySelector('.at-compare__range');
    range?.addEventListener('input', () => this.style.setProperty('--pos', `${range.value}%`));
  }
}
if (!customElements.get('at-compare')) customElements.define('at-compare', AtCompare);

/* Rasterwissel op collectiepagina's */
function initGridSwitch(scope = document) {
  scope.querySelectorAll('[data-grid-switch]').forEach((wrap) => {
    if (wrap.dataset.ready) return;
    wrap.dataset.ready = '1';
    const grid = document.getElementById('product-grid');
    const saved = (() => {
      try {
        return localStorage.getItem('at-grid');
      } catch (e) {
        return null;
      }
    })();
    const apply = (cols) => {
      const list = document.getElementById('product-grid');
      if (!list) return;
      list.dataset.cols = cols;
      wrap.querySelectorAll('button').forEach((b) => b.classList.toggle('is-active', b.dataset.cols === cols));
    };
    wrap.querySelectorAll('button').forEach((btn) =>
      btn.addEventListener('click', () => {
        apply(btn.dataset.cols);
        try {
          localStorage.setItem('at-grid', btn.dataset.cols);
        } catch (e) {}
      })
    );
    if (grid && saved) apply(saved);
  });
}

function initAtelier(scope = document) {
  scope.querySelectorAll('.at-slideshow').forEach((el) => {
    if (!el.atSlideshow) el.atSlideshow = new AtSlideshow(el);
  });
  initGridSwitch(scope);
}

document.addEventListener('DOMContentLoaded', () => initAtelier());
document.addEventListener('shopify:section:load', (e) => initAtelier(e.target));
document.addEventListener('shopify:block:select', (e) => {
  const slide = e.target.closest('.at-slideshow__slide');
  const root = slide?.closest('.at-slideshow');
  if (root?.atSlideshow) {
    root.atSlideshow.stop();
    root.atSlideshow.show(+slide.dataset.index);
  }
  const card = e.target.closest('.at-carousel__slide');
  if (card) card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  const look = e.target.closest('at-look');
  if (look && e.target.dataset.go) look.show(+e.target.dataset.go);
});
/* Na filteren/sorteren vervangt Dawn de productlijst: kolomkeuze opnieuw toepassen */
document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('ProductGridContainer');
  if (!container) return;
  new MutationObserver(() => {
    const list = document.getElementById('product-grid');
    const active = document.querySelector('[data-grid-switch] .is-active');
    if (list && active && list.dataset.cols !== active.dataset.cols) list.dataset.cols = active.dataset.cols;
  }).observe(container, { childList: true, subtree: true });
});

/* Filterlade: filters werken als gewone accordeons (open standaard) i.p.v. Dawn's submenu's */
function initFacetAccordions() {
  document.querySelectorAll('#FacetsWrapperMobile .mobile-facets__details').forEach((details) => {
    if (details.dataset.atAccordion) return;
    details.dataset.atAccordion = '1';
    details.open = true;
    details.querySelector(':scope > summary')?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        details.open = !details.open;
      },
      true
    );
  });
}
document.addEventListener('DOMContentLoaded', () => {
  initFacetAccordions();
  const wrapper = document.getElementById('main-collection-filters');
  if (wrapper) new MutationObserver(initFacetAccordions).observe(wrapper, { childList: true, subtree: true });
});


/* Foto-tekstblok op desktop: foto blijft staan en wisselt mee met de tekst in beeld */
class AtScrollRows {
  constructor(root) {
    this.root = root;
    this.texts = [...root.querySelectorAll('.at-rows__content')];
    this.images = [...root.querySelectorAll('.at-rows__media')];
    this.visible = 0;
    this.desktop = window.matchMedia('(min-width: 990px)');
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll);
    this.desktop.addEventListener('change', this.onScroll);
    this.onScroll();
  }

  onScroll() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  update() {
    if (!this.desktop.matches) {
      this.texts.forEach((t) => (t.style.opacity = ''));
      this.images.forEach((img) => img.classList.remove('is-visible'));
      return;
    }
    const vh = window.innerHeight;
    // Tekst: 0 → 0,25 → 1 → 0,25 → 0 terwijl hij door het scherm schuift
    const stops = [0, 0.25, 1, 0.25, 0];
    this.texts.forEach((text) => {
      const r = text.getBoundingClientRect();
      const p = Math.min(Math.max((vh - r.top) / (vh + r.height), 0), 1) * 4;
      const i = Math.min(Math.floor(p), 3);
      text.style.opacity = stops[i] + (stops[i + 1] - stops[i]) * (p - i);
    });
    // Foto: welke tekst staat in het midden van het scherm
    const box = this.root.getBoundingClientRect();
    const progress = Math.min(Math.max((vh / 2 - box.top) / box.height, 0), 0.9999);
    const index = Math.floor(progress * this.texts.length);
    if (index !== this.visible || !this.images[index]?.classList.contains('is-visible')) {
      this.visible = index;
      this.images.forEach((img, n) => img.classList.toggle('is-visible', n === index));
    }
  }
}

/* Productlijsten: kaarten schuiven één voor één omhoog in beeld */
function initProductReveal(scope = document) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || Shopify.designMode) return;
  const lists = scope.querySelectorAll('.at-product-track, #product-grid, .product-grid, #predictive-search-results-products-list');
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        [...entry.target.children].forEach((card, i) => {
          card.style.transitionDelay = `${0.4 + i * 0.05}s`;
          card.classList.add('is-revealed');
        });
      });
    },
    { threshold: 0.1 }
  );
  lists.forEach((list) => {
    if (list.dataset.atReveal) return;
    list.dataset.atReveal = '1';
    [...list.children].forEach((card) => card.classList.add('at-reveal'));
    observer.observe(list);
  });
}

function initScrollRows(scope = document) {
  scope.querySelectorAll('[data-scroll-rows]').forEach((el) => {
    if (!el.atScrollRows) el.atScrollRows = new AtScrollRows(el);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initScrollRows();
  initProductReveal();
});
document.addEventListener('shopify:section:load', (e) => {
  initScrollRows(e.target);
  initProductReveal(e.target);
});

/* Dit heb je bekeken: productpagina's onthouden, kaarten ophalen via de Section Rendering API */
(() => {
  const KEY = 'at-viewed';
  const read = () => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch (e) {
      return [];
    }
  };
  const current = document.querySelector('[data-at-product]')?.dataset.atProduct;
  if (current) {
    const list = [current, ...read().filter((h) => h !== current)].slice(0, 12);
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {}
  }

  class AtRecentlyViewed extends HTMLElement {
    connectedCallback() {
      if (this.loaded) return;
      this.loaded = true;
      this.render();
    }

    async render() {
      const limit = parseInt(this.dataset.limit, 10) || 4;
      const handles = read()
        .filter((h) => h && h !== this.dataset.exclude)
        .slice(0, limit);
      const listEl = this.querySelector('[data-list]');
      if (!handles.length || !listEl) {
        if (this.hasAttribute('data-design-mode')) this.hidden = false;
        return;
      }
      const root = window.Shopify?.routes?.root || '/';
      const cards = await Promise.all(
        handles.map((h) =>
          fetch(`${root}products/${encodeURIComponent(h)}?section_id=atelier-card-ajax`)
            .then((r) => (r.ok ? r.text() : ''))
            .then((html) => new DOMParser().parseFromString(html, 'text/html').querySelector('.at-card-ajax'))
            .catch(() => null)
        )
      );
      cards.filter(Boolean).forEach((card) => {
        const li = document.createElement('li');
        li.className = 'at-recent__item grid__item';
        li.append(...card.childNodes);
        listEl.append(li);
      });
      if (listEl.children.length) this.hidden = false;
    }
  }
  if (!customElements.get('at-recently-viewed')) customElements.define('at-recently-viewed', AtRecentlyViewed);
})();

/* Subcategoriepagina: producten uit een collectie filteren op woorden in de titel */
class AtSubcategory extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;
    const params = new URLSearchParams(location.search);
    this.words = (params.get('q') || '')
      .toLowerCase()
      .split(',')
      .map((w) => w.trim())
      .filter(Boolean);
    const title = (params.get('titel') || '').trim();
    if (title) {
      this.querySelector('[data-sub-title]').textContent = title;
      this.querySelector('[data-sub-crumb]').textContent = title;
      document.title = document.title.replace(/^[^–|-]+/, title + ' ');
    }
    this.perPage = parseInt(this.dataset.perPage, 10) || 24;
    this.shown = 0;
    this.grid = this.querySelector('[data-sub-grid]');
    this.more = this.querySelector('[data-sub-more]');
    this.sortEl = this.querySelector('[data-sub-sort]');
    this.more.addEventListener('click', () => this.renderNext());
    this.sortEl.addEventListener('change', () => this.apply());
    this.load();
  }

  async fetchAll() {
    const key = `at-sub:${this.dataset.collection}`;
    try {
      const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (cached && Date.now() - cached.t < 10 * 60 * 1000) return cached.products;
    } catch (e) {}
    const base = `${this.dataset.root.replace(/\/$/, '')}/collections/${this.dataset.collection}?view=atdata`;
    const get = (page) => fetch(`${base}&page=${page}`).then((r) => r.json());
    const first = await get(1);
    const rest = await Promise.all(Array.from({ length: Math.max(0, first.pages - 1) }, (_, i) => get(i + 2)));
    const products = [first, ...rest].flatMap((p) => p.products);
    try {
      sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), products }));
    } catch (e) {}
    return products;
  }

  async load() {
    try {
      const all = await this.fetchAll();
      this.all = this.words.length ? all.filter((p) => this.words.some((w) => p.t.toLowerCase().includes(w))) : all;
    } catch (e) {
      this.all = [];
    }
    this.querySelector('[data-sub-loading]').hidden = true;
    this.apply();
  }

  apply() {
    const list = [...this.all];
    const s = this.sortEl.value;
    if (s === 'price-ascending') list.sort((a, b) => a.p - b.p);
    if (s === 'price-descending') list.sort((a, b) => b.p - a.p);
    if (s === 'created-descending') list.sort((a, b) => b.d - a.d);
    if (s === 'title-ascending') list.sort((a, b) => a.t.localeCompare(b.t, 'nl'));
    this.list = list;
    this.shown = 0;
    this.grid.innerHTML = '';
    const n = list.length;
    const label = n === 1 ? this.dataset.labelCountOne : this.dataset.labelCountOther;
    this.querySelector('[data-sub-count]').textContent = label.replace('__N__', n);
    this.querySelector('[data-sub-empty]').hidden = n > 0;
    this.renderNext();
  }

  renderNext() {
    const next = this.list.slice(this.shown, this.shown + this.perPage);
    this.grid.insertAdjacentHTML('beforeend', next.map((p) => this.card(p)).join(''));
    this.shown += next.length;
    this.more.hidden = this.shown >= this.list.length;
  }

  card(p) {
    const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
    const src = (u, w) => (u ? u.replace(/width=\d+/, `width=${w}`) : '');
    const set = (u) => [360, 533, 720, 940].map((w) => `${src(u, w)} ${w}w`).join(', ');
    const sizes = '(min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)';
    const img = (u, extra) =>
      `<img src="${src(u, 533)}" srcset="${set(u)}" sizes="${sizes}" alt="${esc(p.t)}" loading="lazy" width="${p.w}" height="${p.hh}" class="motion-reduce"${extra || ''}>`;
    const url = `${this.dataset.root.replace(/\/$/, '')}/products/${p.h}`;
    let badges = '';
    if (!p.a) badges += `<span class="badge badge--bottom-left color-${this.dataset.soldoutScheme}">${esc(this.dataset.labelSoldOut)}</span>`;
    if (p.s) badges += `<span class="badge badge--bottom-left color-${this.dataset.saleScheme}">${esc(this.dataset.labelSave.replace('__AMOUNT__', p.sv))}</span>`;
    const priceCls = `price${p.s ? ' price--on-sale' : ''}${!p.a ? ' price--sold-out' : ''}`;
    return `<li class="grid__item"><div class="card-wrapper product-card-wrapper underline-links-hover"><div class="card card--standard card--media" style="--ratio-percent: 100%;"><div class="card__inner color-scheme-1 gradient ratio" style="--ratio-percent: 100%;"><div class="card__media"><div class="media media--transparent media--hover-effect">${p.i ? img(p.i) : ''}${p.i2 ? img(p.i2) : ''}</div></div><div class="card__content"><div class="card__information"><h3 class="card__heading"><a href="${url}" class="full-unstyled-link">${esc(p.t)}</a></h3></div><div class="card__badge ${this.dataset.badge}">${badges}</div></div></div><div class="card__content"><div class="card__information"><h3 class="card__heading h5"><a href="${url}" class="full-unstyled-link">${esc(p.t)}</a></h3><div class="card-information"><div class="${priceCls}"><div class="price__container"><div class="price__regular"><span class="price-item price-item--regular">${esc(p.pf)}</span></div><div class="price__sale"><span><s class="price-item price-item--regular">${p.cf ? esc(p.cf) : ''}</s></span><span class="price-item price-item--sale price-item--last">${esc(p.pf)}</span></div></div></div></div></div><div class="card__badge ${this.dataset.badge}">${badges}</div></div></div></div></li>`;
  }
}
if (!customElements.get('at-subcategory')) customElements.define('at-subcategory', AtSubcategory);
